// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Dictionary } from "@insite/client-framework/Common/Types";
import { ApiParameter, post, rawRequest } from "@insite/client-framework/Services/ApiService";
import {
    GetProductCollectionApiV2Parameter,
    GetProductCollectionRealTimeInventoryApiV2Parameter,
} from "@insite/client-framework/Services/ProductServiceV2";
import { ProductCollectionModel, RealTimeInventoryModel } from "@insite/client-framework/Types/ApiModels";

const productsUrl = "/api/v2/products";
const realTimeInventoryUrl = "/api/v1/realtimeinventory";

export async function xRequest<T>(
    endpoint: string,
    method: string,
    headers: Dictionary<string> = { "X-Requested-With": "XMLHttpRequest" },
    body?: string | FormData,
    isStatusOkay?: (status: number) => boolean,
    cache: RequestInit["cache"] = "no-cache",
) {
    const response = await rawRequest(endpoint, method, headers, body, isStatusOkay, cache);

    try {
        const status = response.status;
        const res = (await response.json()) as Promise<T>;
        (res as any).status = status;
        return res;
    } catch {
        return {} as Promise<T>;
    }
}

export function xGet<T>(
    endpoint: string,
    parameter: ApiParameter = {},
    queryStringParameters: Dictionary<any> = {},
    cache: RequestInit["cache"] = "no-cache",
) {
    let queryString = "";
    const combinedQueryString: Dictionary<any> = {
        ...parameter,
        ...parameter.additionalQueryStringParameters,
        ...queryStringParameters,
    };

    const additionalExpands = combinedQueryString["additionalExpands"];
    if (additionalExpands) {
        if (!combinedQueryString["expand"]) {
            combinedQueryString["expand"] = [];
        }
        combinedQueryString["expand"] = combinedQueryString["expand"].concat(additionalExpands);
        delete combinedQueryString["additionalExpands"];
    }

    for (const key in combinedQueryString) {
        if (key !== "additionalQueryStringParameters" && combinedQueryString[key] !== undefined) {
            if (key !== "expand" && key !== "exclude" && Array.isArray(combinedQueryString[key])) {
                // WebApi requires arrays to be split into individual query string values
                combinedQueryString[key].forEach((p: string) => {
                    queryString += `${key}=${encodeURIComponent(p)}&`;
                });
            } else {
                queryString += `${key}=${encodeURIComponent(combinedQueryString[key])}&`;
            }
        }
    }

    if (queryString !== "") {
        queryString = (endpoint.indexOf("?") < 0 ? "?" : "&") + queryString.substr(0, queryString.length - 1);
    }

    return xRequest<T>(endpoint + queryString, "GET", undefined, undefined, undefined, cache);
}

export function xGetProductCollectionV2(parameter: GetProductCollectionApiV2Parameter) {
    return xGet<ProductCollectionModel>(productsUrl, parameter);
}

export function xGetProductCollectionRealTimeInventory(parameter: GetProductCollectionRealTimeInventoryApiV2Parameter) {
    let url = realTimeInventoryUrl;
    if (parameter.expand) {
        url += `?expand=${parameter.expand.join(",")}`;
    }

    return post<GetProductCollectionRealTimeInventoryApiV2Parameter, RealTimeInventoryModel>(`${url}`, {
        productIds: parameter.productIds,
        configurations: parameter.configurations,
        // XNG-Changes
        ...((parameter as any)?.properties ? { properties: (parameter as any).properties } : {}),
    });
}
